<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const props = defineProps({
  task: {
    type: Object,
    required: true,
  },
});

defineEmits(['open']);

const { t } = useI18n();

const isDone = computed(() => props.task.status === 'done');
const labels = computed(() => props.task.labels || []);
const coverColor = computed(() => props.task.cover_color || '');
const hasDescription = computed(() => Boolean(props.task.description));
const attachmentsCount = computed(() => props.task.attachments_count || 0);

const PRIORITY_DOT = {
  low: '#64748b',
  medium: '#f59e0b',
  high: '#ef4444',
};
const priorityDot = computed(
  () => PRIORITY_DOT[props.task.priority] || PRIORITY_DOT.low
);
const PRIORITY_LABEL_KEYS = {
  low: 'TASKS.PRIORITY.LOW',
  medium: 'TASKS.PRIORITY.MEDIUM',
  high: 'TASKS.PRIORITY.HIGH',
};
const priorityLabel = computed(() =>
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  t(PRIORITY_LABEL_KEYS[props.task.priority] || PRIORITY_LABEL_KEYS.low)
);

const checklist = computed(() => props.task.checklist || []);
const checklistTotal = computed(() => checklist.value.length);
const checklistDone = computed(
  () => checklist.value.filter(item => item.done).length
);
const checklistLabel = computed(
  () => `${checklistDone.value}/${checklistTotal.value}`
);
const checklistComplete = computed(
  () => checklistTotal.value > 0 && checklistDone.value === checklistTotal.value
);

const dueLabel = computed(() => {
  if (!props.task.due_date) return '';
  const [, month, day] = props.task.due_date.split('-');
  return `${day}/${month}`;
});

const members = computed(() => {
  const list = props.task.assignee ? [props.task.assignee] : [];
  (props.task.members || []).forEach(member => {
    if (!list.some(item => item.id === member.id)) list.push(member);
  });
  return list;
});
const visibleMembers = computed(() => members.value.slice(0, 3));
const extraMembers = computed(() => Math.max(0, members.value.length - 3));
const commentsCount = computed(() => props.task.comments_count || 0);

const localISODate = offsetDays => {
  const date = new Date();
  date.setDate(date.getDate() + offsetDays);
  const pad = n => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const isOverdue = computed(() => {
  if (!props.task.due_date || props.task.status === 'done') return false;
  return props.task.due_date < localISODate(0);
});

const isDueSoon = computed(() => {
  if (!props.task.due_date || props.task.status === 'done') return false;
  return [localISODate(0), localISODate(1)].includes(props.task.due_date);
});
</script>

<template>
  <div
    class="flex flex-col overflow-hidden border rounded-lg cursor-pointer bg-n-solid-1 border-n-weak hover:border-n-strong hover:shadow-md hover:-translate-y-px transition-all"
    @click="$emit('open', task)"
  >
    <div
      v-if="coverColor"
      class="h-8 w-full"
      :style="{ backgroundColor: coverColor }"
    />

    <div class="flex flex-col gap-1.5 p-2.5">
      <div v-if="labels.length" class="flex flex-wrap gap-1">
        <span
          v-for="(label, index) in labels"
          :key="index"
          class="h-2 rounded-full min-w-[2.5rem]"
          :style="{ backgroundColor: label.color }"
          :title="label.name || ''"
        />
      </div>

      <p
        class="flex items-start gap-1.5 text-sm"
        :class="isDone ? 'text-n-slate-10 line-through' : 'text-n-slate-12'"
      >
        <span
          v-if="isDone"
          class="i-lucide-check-circle-2 size-3.5 shrink-0 mt-0.5 text-n-teal-11"
        />
        <span class="line-clamp-3">{{ task.title }}</span>
      </p>

      <div class="flex flex-wrap items-center gap-2.5 text-n-slate-11">
        <span class="flex items-center gap-1 text-xs" :title="priorityLabel">
          <span
            class="rounded-full size-2"
            :style="{ backgroundColor: priorityDot }"
          />
          {{ priorityLabel }}
        </span>

        <span
          v-if="task.due_date"
          class="flex items-center gap-1 text-xs"
          :class="
            isOverdue
              ? 'text-n-ruby-11 font-medium'
              : isDueSoon
                ? 'text-n-amber-11 font-medium'
                : ''
          "
        >
          <span class="i-lucide-clock size-3" />
          {{ dueLabel }}
        </span>

        <span
          v-if="hasDescription"
          class="i-lucide-align-left size-3.5"
          :title="t('TASKS.CARD.DESCRIPTION')"
        />

        <span v-if="attachmentsCount" class="flex items-center gap-1 text-xs">
          <span class="i-lucide-paperclip size-3" />
          {{ attachmentsCount }}
        </span>

        <span
          v-if="checklistTotal"
          class="flex items-center gap-1 px-1 text-xs rounded"
          :class="checklistComplete ? 'text-n-teal-11 bg-n-teal-3' : ''"
        >
          <span class="i-lucide-check-square size-3" />
          {{ checklistLabel }}
        </span>

        <span
          v-if="commentsCount"
          class="flex items-center gap-1 text-xs"
          :title="t('TASKS.CARD.COMMENTS_COUNT', { n: commentsCount })"
        >
          <span class="i-lucide-message-square size-3" />
          {{ commentsCount }}
        </span>

        <div
          v-if="visibleMembers.length"
          class="flex items-center ml-auto -space-x-1.5"
        >
          <Avatar
            v-for="member in visibleMembers"
            :key="member.id"
            :name="member.name"
            :src="member.avatar_url"
            :size="20"
            rounded-full
            class="ring-2 ring-n-solid-1"
          />
          <span v-if="extraMembers" class="pl-2 text-xs text-n-slate-10">{{
            `+${extraMembers}`
          }}</span>
        </div>
      </div>
    </div>
  </div>
</template>
